import React, { useCallback, useEffect, useState } from 'react';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import { errorMessageOf } from '../../utils/apiErrors';
import {
  createKioskPassword, kioskLinkFor, kioskPasswordState, listKioskPasswords, revokeKioskPassword, type KioskPassword,
} from '../../services/KioskAccessService';
import '../../styles/components/KioskAccess.css';

const when = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : 'Never');

/** Copies text; returns false when the browser refuses (the caller then leaves the text selected for a manual copy). */
async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

interface Props {
  eventId: string;
  /** Owner or admin only: the server refuses everyone else, and the section is not shown to them. */
  disabled?: boolean;
}

/**
 * "Kiosk access" on the event form: issue a password for this event's door devices, see it once, list and revoke.
 * A kiosk can only open this one event's guest list, check people in and add walk-ins.
 */
const KioskAccessSection: React.FC<Props> = ({ eventId, disabled = false }) => {
  const [items, setItems] = useState<KioskPassword[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [label, setLabel] = useState('');
  const [expiry, setExpiry] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fresh, setFresh] = useState<{ password: string; label: string } | null>(null);
  const [copied, setCopied] = useState<'password' | 'link' | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const link = kioskLinkFor(eventId);

  const load = useCallback(async () => {
    setLoadError(null);
    try { setItems(await listKioskPasswords(eventId)); } catch (err) { setLoadError(err); setItems((prev) => prev ?? []); }
  }, [eventId]);
  useEffect(() => { void load(); }, [load]);

  const generate = async () => {
    setBusy(true); setFormError(null);
    try {
      // End of the chosen day in the person's own time zone.
      const expires_at = expiry ? new Date(`${expiry}T23:59:00`).toISOString() : undefined;
      const made = await createKioskPassword(eventId, { label: label.trim(), ...(expires_at ? { expires_at } : {}) });
      setFresh({ password: made.password, label: made.label });
      setLabel(''); setExpiry('');
      await load();
    } catch (err) {
      setFormError(errorMessageOf(err, 'Could not make a password. Try again.'));
    } finally { setBusy(false); }
  };

  const revoke = async (id: string) => {
    setBusy(true); setFormError(null);
    try { await revokeKioskPassword(eventId, id); setConfirmRevoke(null); await load(); }
    catch (err) { setFormError(errorMessageOf(err, 'Could not revoke that password. Try again.')); }
    finally { setBusy(false); }
  };

  const copy = async (what: 'password' | 'link', text: string) => {
    if (await copyText(text)) { setCopied(what); setTimeout(() => setCopied(null), 2000); }
  };

  return (
    <section className="kiosk-access" aria-labelledby="kiosk-access-title">
      <h3 id="kiosk-access-title" className="kiosk-access__title">Kiosk access</h3>
      <p className="form-helper">
        A password for the tablets and phones at the door of this event. It opens only this event's guest list, check-in and walk-ins. It shows no money.
      </p>

      <div className="kiosk-access__link">
        <label className="kiosk-access__label" htmlFor="kiosk-link">Link for the door devices</label>
        <div className="kiosk-access__row">
          <input id="kiosk-link" className="kiosk-access__input" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
          <Button type="button" variant="secondary" onClick={() => { void copy('link', link); }}>{copied === 'link' ? 'Copied' : 'Copy link'}</Button>
        </div>
      </div>

      {fresh && (
        <div className="kiosk-access__fresh" role="status">
          <p><strong>Copy this password now.</strong> It is shown only once and cannot be looked up later. If it is lost, revoke it and make a new one.</p>
          <div className="kiosk-access__row">
            <input className="kiosk-access__input kiosk-access__secret" readOnly aria-label="New kiosk password" value={fresh.password} onFocus={(e) => e.currentTarget.select()} />
            <Button type="button" variant="primary" onClick={() => { void copy('password', fresh.password); }}>{copied === 'password' ? 'Copied' : 'Copy password'}</Button>
          </div>
          <Button type="button" variant="secondary" onClick={() => setFresh(null)}>I have saved it</Button>
        </div>
      )}

      {!disabled && (
        <div className="kiosk-access__new">
          <div className="kiosk-access__row">
            <div className="kiosk-access__field">
              <label className="kiosk-access__label" htmlFor="kiosk-label">Label (for example Main door)</label>
              <input id="kiosk-label" className="kiosk-access__input" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div className="kiosk-access__field">
              <label className="kiosk-access__label" htmlFor="kiosk-expiry">Stops working after (optional)</label>
              <input id="kiosk-expiry" className="kiosk-access__input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
            </div>
          </div>
          {formError && <p className="kiosk-access__error" role="alert">{formError}</p>}
          <Button type="button" variant="primary" loading={busy} onClick={() => { void generate(); }}>Generate password</Button>
        </div>
      )}

      {loadError != null && <LoadErrorBox what="kiosk passwords" error={loadError} onRetry={() => { void load(); }} />}
      {items === null && loadError == null && <p className="form-helper" role="status">Loading kiosk passwords…</p>}
      {items !== null && items.length === 0 && loadError == null && <p className="form-helper">No kiosk passwords yet.</p>}
      {items !== null && items.length > 0 && (
        <ul className="kiosk-access__list" aria-label="Kiosk passwords">
          {items.map((p) => {
            const state = kioskPasswordState(p);
            return (
              <li key={p.id} className="kiosk-access__item">
                <div>
                  <div className="kiosk-access__name">{p.label || 'Unnamed password'}</div>
                  <div className="kiosk-access__meta">
                    <span className={`kiosk-access__state kiosk-access__state--${state}`}>{state === 'active' ? 'Active' : state === 'revoked' ? 'Revoked' : 'Expired'}</span>
                    {' '}Made {when(p.created_at)} · Expires {when(p.expires_at)} · Last used {when(p.last_used_at)}
                  </div>
                </div>
                {state === 'active' && !disabled && (confirmRevoke === p.id ? (
                  <div className="kiosk-access__row">
                    <Button type="button" variant="danger" loading={busy} aria-label={`Confirm revoke ${p.label || 'password'}`} onClick={() => { void revoke(p.id); }}>Yes, revoke</Button>
                    <Button type="button" variant="secondary" onClick={() => setConfirmRevoke(null)}>Keep</Button>
                  </div>
                ) : (
                  <Button type="button" variant="secondary" aria-label={`Revoke ${p.label || 'password'}`} onClick={() => setConfirmRevoke(p.id)}>Revoke</Button>
                ))}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default KioskAccessSection;
